import { isStrategyTab } from './strategyTabs';

/**
 * The stable URL of a list tab, or null when it has none.
 *
 * ONE definition shared by ListRouteSync (which navigates to it), the Share button
 * on the list header and the /lists index, so they can never disagree about
 * what a list's URL is. AI portfolios (ptf_*) live under /portfolio/, every
 * other list under /list/. The watchlist maps to the root (/) and never gets a
 * /list URL of its own.
 */
export const getListPath = (id) => {
  if (!id || typeof id !== 'string' || id === 'watchlist') return null;
  return `${id.startsWith('ptf_') ? '/portfolio/' : '/list/'}${id}`;
};

/**
 * Nest a category's flat filter list into a tree by `parent_list`, to any depth.
 * Returns the top-level filters as shallow copies, each with a `children` array, both
 * in their original order. A filter is top-level when it has no parent_list or its
 * parent isn't in this list (an orphan stays visible rather than vanishing). A cycle
 * (A's parent is B and B's is A, or a filter that names itself) is broken at the
 * member that comes first in the original order, which becomes top-level, so every
 * filter appears exactly once. Pure: the input objects are not modified.
 */
export const buildFilterTree = (filters) => {
  const list = Array.isArray(filters) ? filters : [];
  const nodes = list.map((filter) => ({ ...filter, children: [] }));

  // First filter with each id is the one a parent_list refers to.
  const indexById = new Map();
  nodes.forEach((node, i) => {
    if (!indexById.has(node.id)) indexById.set(node.id, i);
  });

  const parentIndex = nodes.map((node, i) => {
    const parent = node.parent_list ? indexById.get(node.parent_list) : undefined;
    return parent === undefined || parent === i ? null : parent;
  });

  // Every node has at most one parent, so a walk up either ends at a root or loops. Cut
  // each loop at its earliest member.
  const state = new Array(nodes.length).fill(0); // 0 unseen, 1 on the current walk, 2 settled
  for (let start = 0; start < nodes.length; start += 1) {
    const walk = [];
    let at = start;
    while (at !== null && state[at] === 0) {
      state[at] = 1;
      walk.push(at);
      at = parentIndex[at];
    }
    if (at !== null && state[at] === 1) {
      const cycle = walk.slice(walk.indexOf(at));
      parentIndex[Math.min(...cycle)] = null;
    }
    walk.forEach((i) => { state[i] = 2; });
  }

  const roots = [];
  nodes.forEach((node, i) => {
    if (parentIndex[i] === null) roots.push(node);
    else nodes[parentIndex[i]].children.push(node);
  });
  return roots;
};

/**
 * Every smart-list filter under `filters`, nested ones included, parents first, as
 * { filter, depth }. useCategories (via buildFilterTree) moves a filter that has a
 * parent_list out of category.filters and into its parent's `children`, so a plain
 * scan of category.filters misses it.
 */
export const flattenFilters = (filters, depth = 0) =>
  (Array.isArray(filters) ? filters : []).flatMap((filter) => [
    { filter, depth },
    ...flattenFilters(filter.children, depth + 1),
  ]);

/**
 * The smart-list filter with this id, nested ones included, or undefined. The one lookup
 * behind isRoutableListId / isShareableListId, the list title (App.js, ListSEO) and the
 * explanation row, so none of them needs to know about `children`.
 */
export const findSmartList = (categories, id) => {
  if (!Array.isArray(categories)) return undefined;
  for (const category of categories) {
    const match = flattenFilters(category.filters).find(({ filter }) => filter.id === id);
    if (match) return match.filter;
  }
  return undefined;
};

const isSmartListId = (id, categories) => !!findSmartList(categories, id);

/**
 * Does this tab have a stable, browsable URL? Pure version of what
 * ListRouteSync accepts from /list/<id> and /portfolio/<id>: AI portfolios and
 * strategy tabs (both fetched separately), any smart-list filter (nested ones too), any of the
 * user's own lists. The watchlist is excluded — it has no /list URL — and so
 * are the virtual tabs (url_symbols, symbols_*, earnings_day_*, label_*), which
 * are never in categories or the user's lists.
 */
export const isRoutableListId = (id, categories, userListsData) => {
  if (!id || typeof id !== 'string' || id === 'watchlist') return false;

  if (id.startsWith('ptf_') || isStrategyTab(id)) return true;

  if (isSmartListId(id, categories)) return true;

  return Array.isArray(userListsData) && userListsData.some((list) => list.id === id);
};

/**
 * The is_public flag as the API sends it: D1 returns 1 / 0, but accept true and '1' too.
 * Missing, null and false are not public.
 */
export const isPublicFlag = (value) => Boolean(Number(value));

/**
 * May a user list (an item of userListsData) be shared? Only a public one, and never a
 * custom filter: getUserLists returns `is_public` lists to EVERY signed-in user, so the
 * list is in the viewer's userListsData and ListRouteSync resolves its URL, but a custom
 * filter's results are evaluated per OWNER (fetchCustomFilterResults takes the owner's
 * id), so for anyone else it doesn't load. One predicate, shared by the header Share button and
 * the /lists rows, so they can't disagree.
 */
export const isShareableUserList = (list) =>
  !!list && isPublicFlag(list.is_public) && list.type !== 'custom_filter';

/**
 * May this list's link be handed to someone else? Smart lists: every viewer's app
 * resolves those. A public user list that isn't a custom filter (see isShareableUserList).
 * Anything else — a private user list (its owner, plus anyone it was shared with), a
 * public custom filter, a strategy, the virtual tabs — doesn't work for other people
 * (ListRouteSync validates against the VIEWER's own lists and strategies, so anyone else
 * lands on their default tab), and portfolios have their own is_public / followed rules.
 * This gates the share controls only — routing is isRoutableListId, which stays broader.
 * `userListsData` is optional: without it only smart lists qualify.
 */
export const isShareableListId = (id, categories, userListsData) => {
  if (!id || typeof id !== 'string') return false;
  if (isSmartListId(id, categories)) return true;
  return Array.isArray(userListsData)
    && userListsData.some((list) => list.id === id && isShareableUserList(list));
};
